import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  X,
  UserPlus,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

interface CreateAccessFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  employeeId: string;
  accessType: string;
  systemsRequested: string[];
  accessLevel: string;
  justification: string;
  jobRole: string;
  startDate: string;
  endDate: string;
  isPermanent: boolean;
  notes: string;
}

interface FormErrors {
  employeeId?: string;
  accessType?: string;
  systemsRequested?: string;
  accessLevel?: string;
  justification?: string;
  jobRole?: string;
  startDate?: string;
}

const COMMON_SYSTEMS = [
  'Email / Microsoft 365',
  'Active Directory',
  'VPN Access',
  'SharePoint',
  'ERP System',
  'CRM System',
  'HR Portal',
  'Finance System',
  'Project Management',
  'Development Tools',
  'Database Access',
  'Cloud Console',
];

export default function CreateAccessFormModal({ isOpen, onClose, onSuccess }: CreateAccessFormModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [formData, setFormData] = useState<FormData>({
    employeeId: '',
    accessType: 'NEW_HIRE',
    systemsRequested: [],
    accessLevel: 'READ',
    justification: '',
    jobRole: '',
    startDate: '',
    endDate: '',
    isPermanent: true,
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [customSystem, setCustomSystem] = useState('');

  // Fetch employees for selection
  const { data: employeesData, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=200');
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = {
        ...data,
        systemsRequested: JSON.stringify(data.systemsRequested),
        lineManagerId: user?.id,
        lineManagerName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      };
      const response = await api.post('/access-forms', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForms'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to create access form');
    },
  });

  const employees = employeesData || [];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.employeeId) {
      newErrors.employeeId = 'Please select an employee';
    }

    if (!formData.accessType) {
      newErrors.accessType = 'Please select an access type';
    }

    if (formData.systemsRequested.length === 0) {
      newErrors.systemsRequested = 'Please select at least one system';
    }

    if (!formData.accessLevel) {
      newErrors.accessLevel = 'Please select an access level';
    }

    if (!formData.justification.trim()) {
      newErrors.justification = 'Justification is required';
    }

    if (!formData.jobRole.trim()) {
      newErrors.jobRole = 'Job role is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    createMutation.mutate(formData);
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleSystem = (system: string) => {
    setFormData(prev => ({
      ...prev,
      systemsRequested: prev.systemsRequested.includes(system)
        ? prev.systemsRequested.filter(s => s !== system)
        : [...prev.systemsRequested, system],
    }));
    if (errors.systemsRequested) {
      setErrors(prev => ({ ...prev, systemsRequested: undefined }));
    }
  };

  const addCustomSystem = () => {
    if (customSystem.trim() && !formData.systemsRequested.includes(customSystem.trim())) {
      setFormData(prev => ({
        ...prev,
        systemsRequested: [...prev.systemsRequested, customSystem.trim()],
      }));
      setCustomSystem('');
      if (errors.systemsRequested) {
        setErrors(prev => ({ ...prev, systemsRequested: undefined }));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      accessType: 'NEW_HIRE',
      systemsRequested: [],
      accessLevel: 'READ',
      justification: '',
      jobRole: '',
      startDate: '',
      endDate: '',
      isPermanent: true,
      notes: '',
    });
    setErrors({});
    setSubmitError(null);
    setCustomSystem('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            <h2 className="text-xl font-semibold">New Access Form</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Error Alert */}
          {submitError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <p className="text-destructive">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Selection */}
            <div className="space-y-2">
              <Label htmlFor="employeeId">
                Employee <span className="text-destructive">*</span>
              </Label>
              <select
                id="employeeId"
                className={cn(
                  "w-full h-10 px-3 rounded-md border bg-background text-sm",
                  errors.employeeId ? "border-destructive" : "border-input"
                )}
                value={formData.employeeId}
                onChange={(e) => handleChange('employeeId', e.target.value)}
                disabled={isLoadingEmployees}
              >
                <option value="">Select an employee</option>
                {employees.map((employee: any) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.firstName} {employee.lastName} - {employee.email}
                  </option>
                ))}
              </select>
              {errors.employeeId && (
                <p className="text-sm text-destructive">{errors.employeeId}</p>
              )}
            </div>

            {/* Access Type */}
            <div className="space-y-2">
              <Label>
                Access Type <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'NEW_HIRE', label: 'New Hire', desc: 'New employee onboarding' },
                  { value: 'ROLE_CHANGE', label: 'Role Change', desc: 'Position or team change' },
                  { value: 'TEMPORARY', label: 'Temporary', desc: 'Time-limited access' },
                  { value: 'CONTRACTOR', label: 'Contractor', desc: 'External contractor' },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      formData.accessType === type.value
                        ? "border-primary bg-primary/5"
                        : "border-input hover:border-muted-foreground/50"
                    )}
                    onClick={() => handleChange('accessType', type.value)}
                  >
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-muted-foreground">{type.desc}</p>
                  </button>
                ))}
              </div>
              {errors.accessType && (
                <p className="text-sm text-destructive">{errors.accessType}</p>
              )}
            </div>

            {/* Job Role */}
            <div className="space-y-2">
              <Label htmlFor="jobRole">
                Job Role <span className="text-destructive">*</span>
              </Label>
              <Input
                id="jobRole"
                placeholder="e.g., Software Engineer, Sales Manager"
                value={formData.jobRole}
                onChange={(e) => handleChange('jobRole', e.target.value)}
                error={errors.jobRole}
              />
            </div>

            {/* Access Level */}
            <div className="space-y-2">
              <Label>
                Access Level <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'READ', label: 'Read Only', color: 'bg-gray-100 text-gray-800' },
                  { value: 'WRITE', label: 'Read/Write', color: 'bg-yellow-100 text-yellow-800' },
                  { value: 'ADMIN', label: 'Admin', color: 'bg-red-100 text-red-800' },
                ].map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    className={cn(
                      "p-3 rounded-lg border-2 transition-all",
                      formData.accessLevel === level.value
                        ? "border-primary bg-primary/5"
                        : "border-input hover:border-muted-foreground/50"
                    )}
                    onClick={() => handleChange('accessLevel', level.value)}
                  >
                    <span className={cn("px-2 py-1 text-xs font-medium rounded-full", level.color)}>
                      {level.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Systems Requested */}
            <div className="space-y-2">
              <Label>
                Systems Requested <span className="text-destructive">*</span>
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[60px]">
                {COMMON_SYSTEMS.map((system) => (
                  <button
                    key={system}
                    type="button"
                    className={cn(
                      "px-3 py-1 text-sm rounded-full transition-all",
                      formData.systemsRequested.includes(system)
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-muted/80"
                    )}
                    onClick={() => toggleSystem(system)}
                  >
                    {system}
                  </button>
                ))}
              </div>
              
              {/* Custom systems added */}
              {formData.systemsRequested.filter(s => !COMMON_SYSTEMS.includes(s)).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.systemsRequested
                    .filter(s => !COMMON_SYSTEMS.includes(s))
                    .map((system) => (
                      <span
                        key={system}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-primary text-primary-foreground"
                      >
                        {system}
                        <button
                          type="button"
                          onClick={() => toggleSystem(system)}
                          className="hover:bg-primary-foreground/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                </div>
              )}

              {/* Add custom system */}
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add custom system..."
                  value={customSystem}
                  onChange={(e) => setCustomSystem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomSystem();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addCustomSystem}>
                  Add
                </Button>
              </div>
              
              {errors.systemsRequested && (
                <p className="text-sm text-destructive">{errors.systemsRequested}</p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  Start Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  error={errors.startDate}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  disabled={formData.isPermanent}
                />
              </div>
            </div>

            {/* Permanent Access */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPermanent"
                checked={formData.isPermanent}
                onChange={(e) => {
                  handleChange('isPermanent', e.target.checked);
                  if (e.target.checked) {
                    handleChange('endDate', '');
                  }
                }}
                className="w-4 h-4 rounded border-input"
              />
              <Label htmlFor="isPermanent" className="font-normal cursor-pointer">
                Permanent access (no end date)
              </Label>
            </div>

            {/* Justification */}
            <div className="space-y-2">
              <Label htmlFor="justification">
                Justification <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="justification"
                className={cn(
                  "w-full min-h-[100px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                  errors.justification ? "border-destructive focus:ring-destructive" : "border-input"
                )}
                placeholder="Explain why this access is needed..."
                value={formData.justification}
                onChange={(e) => handleChange('justification', e.target.value)}
              />
              {errors.justification && (
                <p className="text-sm text-destructive">{errors.justification}</p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <textarea
                id="notes"
                className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Any additional information..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            loading={createMutation.isPending}
          >
            Create Access Form
          </Button>
        </div>
      </div>
    </div>
  );
}
